import {
  createController,
  currentMonthFirst,
  emptyOptionSearch,
  optionQueryAction,
  parseMonth,
} from '@eo/shared';

import {
  createTimeOff,
  fetchOwnedClients,
  fetchStaffNames,
  fetchUserCapacity,
  removeTimeOff,
  searchClients,
  setClientOwner,
  upsertSplit,
} from './api/api';
import type { SplitAllocations } from './api/types';
import type { UserDetailState } from './types';

const initialState: UserDetailState = {
  month: currentMonthFirst(),
  capacity: null,
  clients: [],
  clientsPagination: null,
  staff: [],
  options: { clients: emptyOptionSearch() },
};

export const userDetailController = createController<UserDetailState>({
  name: 'userDetail',
  state: initialState,
  fetchData: async ({ params, queryParams }) => {
    const id = (params as { id?: string } | null)?.id ?? '';
    const query = (queryParams ?? {}) as { month?: string; page?: string };
    const month = parseMonth(query.month);
    const page = Number(query.page) > 0 ? Number(query.page) : undefined;
    const [capacity, owned, staff] = await Promise.all([
      fetchUserCapacity(id, month),
      fetchOwnedClients(id, page),
      fetchStaffNames(),
    ]);
    return { month, capacity, clients: owned.clients, clientsPagination: owned.pagination, staff };
  },
  asyncActions: {
    queryOptions: optionQueryAction<UserDetailState>({
      clients: async (params) => {
        const { clients, pagination } = await searchClients(params);
        return { items: clients, pagination };
      },
    }),
    // Every mutation refetches the one member rather than patching state, the
    // same reasoning ClientDetail uses: the server is the single source for
    // what "in force this month" means.
    upsertSplit: {
      action: async (payload) => {
        const body = payload as { userId: string; month: string; allocations: SplitAllocations };
        await upsertSplit(body);
        return { capacity: await fetchUserCapacity(body.userId, body.month) };
      },
      handlers: { onSuccess: refreshed },
    },
    createTimeOff: {
      action: async (payload) => {
        const body = payload as {
          userId: string;
          date: string;
          hours?: number;
          notes?: string;
          month: string;
        };
        await createTimeOff(body);
        return { capacity: await fetchUserCapacity(body.userId, body.month) };
      },
      handlers: { onSuccess: refreshed },
    },
    // Ownership is a client-row field, so this refetches the client list rather
    // than the member: the member's own capacity did not change.
    setClientOwner: {
      action: async (payload) => {
        const { clientId, ownerUserId, memberId } = payload as {
          clientId: string;
          ownerUserId: string | null;
          memberId: string;
        };
        await setClientOwner(clientId, ownerUserId);
        return fetchOwnedClients(memberId, undefined);
      },
      handlers: {
        onSuccess: (state: UserDetailState, { action }: { action: unknown }): UserDetailState => {
          const owned = (action as { payload: Awaited<ReturnType<typeof fetchOwnedClients>> })
            .payload;
          return { ...state, clients: owned.clients, clientsPagination: owned.pagination };
        },
      },
    },
    removeTimeOff: {
      action: async (payload) => {
        const { id, userId, month } = payload as { id: string; userId: string; month: string };
        await removeTimeOff(id);
        return { capacity: await fetchUserCapacity(userId, month) };
      },
      handlers: { onSuccess: refreshed },
    },
  },
});

function refreshed(state: UserDetailState, { action }: { action: unknown }): UserDetailState {
  return {
    ...state,
    capacity: (action as { payload: { capacity: UserDetailState['capacity'] } }).payload.capacity,
  };
}
